import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * hallmark critique · AppShell
 * tokens locked from docs/design.md §3 (spacing) + §6 (band alternation)
 * states covered: connecting · connected · polling · disconnected (delegated to Header dot)
 *                 idle / loading / empty / error for children (via nested components)
 *                 nav-tab: overview · demand · sim · docs (Phase 6 branch)
 * mobile verified: 320 / 375 / 414 / 768
 * no fake chrome · no italic headers · no shadows · single clay accent per viewport
 *
 * Grid layout: header row · main row (map + right rail) · bottom strip.
 * Phase 6: `activeNav` swaps the right rail between `BunchingRail`
 * (overview) and `PrivacyPanel` (demand), and mounts `DemandHeatmap` +
 * `HeatmapLegend` on the map when demand is active. When demand is
 * active, VehicleLayer is instructed to render every vehicle in its
 * "normal" style so the clay accent stays owned by the heatmap layer.
 * The `phantomLayerSlot` prop from Phase 7 is passed through to BusMap
 * unchanged — Phase 6 accepts + forwards it, but does not populate it.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toSimTraceStub } from "../../types/simTrace";
import { Header } from "./Header";
import { BottomStrip } from "./BottomStrip";
import { BusMap } from "../map/BusMap";
import { BunchingRail } from "../alerts/BunchingRail";
import { HoldCommandPanel } from "../hold/HoldCommandPanel";
import { useHoldCommand } from "../../hooks/useHoldCommand";
import { SimControls } from "../sim/SimControls";
import { ABChart } from "../sim/ABChart";
import { PrivacyPanel } from "../demand/PrivacyPanel";
import { DocsPanel } from "../docs/DocsPanel";
import { useDemandHeatmap } from "../../hooks/useDemandHeatmap";
import { PhantomLayer } from "../map/PhantomLayer";
import { PhantomEventStrip } from "../phantom/PhantomEventStrip";
import { RailCarousel } from "../rail/RailCarousel";
import { useCarpool } from "../../hooks/useCarpool";
import { useCarpoolRequests } from "../../hooks/useCarpoolRequests";
import { PoolMergeSplash } from "../rail/PoolMergeCard";
import { DELHI_CENTER, MUMBAI_CENTER } from "../../lib/tokens";
import { CctvCard } from "../rail/CctvCard";
import { CarpoolTrustCard } from "../rail/CarpoolTrustCard";
import { DriverAutopilotCard } from "../rail/DriverAutopilotCard";
import { RideRequestsCard } from "../rail/RideRequestsCard";
import { BusCctvModal } from "../rail/BusCctvModal";
import { CopilotCard } from "../copilot/CopilotCard";
import { MockPill } from "../common/MockPill";
const VALID_HOLD_STATES = [
    "idle",
    "arming",
    "armed",
    "sending",
    "sent-ack",
    "ack-timeout",
    "error",
    "disabled",
];
/**
 * Test hook: allow Playwright to force the Hold panel into any of the
 * eight states via `?hold=<state>` so we can capture every visual
 * without simulating a 15-step operator interaction.
 */
function readForcedHoldState() {
    if (typeof window === "undefined")
        return undefined;
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("hold");
    if (!raw)
        return undefined;
    return VALID_HOLD_STATES.includes(raw)
        ? raw
        : undefined;
}
/**
 * Empty envelope so the shell renders while the WS is still connecting.
 * D7: mark simulated=false so BusMap centers on Delhi from the first paint
 * — no more Mumbai→Delhi flash when the real OTD envelope arrives ~2s later.
 * (The backend also degrades to the mock on cold-boot failure, so seeing
 * simulated=false here doesn't lie: the very next envelope will confirm.)
 */
function emptyEnvelope() {
    return {
        simulated: false,
        generated_at: new Date(0).toISOString(),
        entities: [],
        alerts: [],
    };
}
const rootStyle = {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    // Lock the shell to the viewport so scrolling the right rail does NOT
    // scroll the whole page (which would drag the map out of view). Each
    // internal pane (rail carousel, alert list) manages its own overflow.
    height: "100vh",
    overflow: "hidden",
    background: "var(--color-pure-white)",
};
const mainStyle = {
    flex: "1 1 auto",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 400px",
    // `min-height: 0` allows this flex child to shrink below its intrinsic
    // content size, and `overflow: hidden` prevents grid children (map,
    // rail carousel) from spilling out and pushing the BottomStrip off
    // the viewport. Without these, the BottomStrip would visually overlap
    // the right rail's alert list on shorter viewports.
    minHeight: 0,
    overflow: "hidden",
};
const mapPaneStyle = {
    position: "relative",
    // `minHeight: 420` here previously forced the grid row to be at least
    // 420px tall even when `<main>` should shrink. That conflict was one
    // half of the BottomStrip overlap bug (the other half was BottomStrip
    // being allowed to flex-shrink). Cap the pane to the grid row instead
    // and let the map fill 100% of whatever height <main> resolves to.
    height: "100%",
    minHeight: 0,
    minWidth: 0,
    overflow: "hidden",
};
export function AppShell({ liveEnvelope, connectionState, connectionError, reconnectAttempts, alerts, daganzoTrace, mpcTrace, tracesLoading, tracesError, activeNav = "overview", onNavChange, phantomLayerSlot = null, phantom, }) {
    // Phase 7: when a phantom feed is provided, mount PhantomLayer into the
    // map's slot and PhantomEventStrip above the bottom strip's 3-col grid.
    const resolvedPhantomLayer = phantom ? (_jsx(PhantomLayer, { activeMatches: phantom.activeMatches })) : (phantomLayerSlot);
    const phantomStrip = phantom ? (_jsx(PhantomEventStrip, { recentMatches: phantom.recentMatches, activeMatches: phantom.activeMatches, connectionState: phantom.connectionState, lastError: phantom.lastError })) : null;
    const [activeController, setActiveController] = useState("mpc-lambda");
    // Q4: track ack time-of-flip so we can prune entries > 15 min. Prevents
    // unbounded growth over long sessions AND lets re-issued alerts render
    // as fresh instead of frozen as "acknowledged".
    const [ackedAt, setAckedAt] = useState(() => new Map());
    const ackedIds = useMemo(() => new Set(ackedAt.keys()), [ackedAt]);
    useEffect(() => {
        const interval = window.setInterval(() => {
            const cutoff = Date.now() - 15 * 60 * 1000;
            setAckedAt((prev) => {
                let dirty = false;
                const next = new Map();
                for (const [id, ts] of prev) {
                    if (ts >= cutoff)
                        next.set(id, ts);
                    else
                        dirty = true;
                }
                return dirty ? next : prev;
            });
        }, 60_000);
        return () => window.clearInterval(interval);
    }, []);
    const [selectedBus, setSelectedBus] = useState(null);
    // Bottom strip is now a floating overlay drawer, default collapsed so the
    // map (our USP) claims the full viewport height. Toggled via a floating
    // pill in the bottom-right corner. Persisted per-session in localStorage
    // so a presenter can lock it open across page refreshes during a demo.
    const [stripOpen, setStripOpen] = useState(() => {
        if (typeof window === "undefined")
            return false;
        return window.localStorage.getItem("bb.stripOpen") === "true";
    });
    useEffect(() => {
        if (typeof window === "undefined")
            return;
        window.localStorage.setItem("bb.stripOpen", stripOpen ? "true" : "false");
    }, [stripOpen]);
    // Phase 16 · 2026-07-28 — swapped from stock stop-cam clips to a
    // single real bus-stop clip supplied by Sayuj (stop-cam-real-1.mp4).
    // Only one real clip is available; keep the choice stable per mount
    // so the label + fallback line up.
    const stopCamClip = "stop-cam-real-1";
    const stopCamTarget = { label: "AS-4-S04 · JB Nagar", fallback: 7 };
    // Phase 12 — Carpool overlay is anchored on the map centre (Delhi for
    // live, Mumbai for mock). Fetched once when data lands; refetch is
    // available via the trust card's local pool.
    const isLive = !(liveEnvelope?.simulated ?? true);
    const carpoolAnchor = useMemo(() => isLive
        ? { lat: DELHI_CENTER[0], lng: DELHI_CENTER[1] }
        : { lat: MUMBAI_CENTER[0], lng: MUMBAI_CENTER[1] }, [isLive]);
    const carpool = useCarpool({
        lat: carpoolAnchor.lat,
        lng: carpoolAnchor.lng,
        count: 6,
    });
    // Phase D — hoisted so RideRequestsCard and BusMap share one WS + one
    // state tree (pending queue + active/moving rides).
    const carpoolRequests = useCarpoolRequests(true);
    const forcedHold = useMemo(readForcedHoldState, []);
    const envelope = liveEnvelope ?? emptyEnvelope();
    // Phase 6 — Demand heatmap poll runs only when the Demand tab is active.
    const demandActive = activeNav === "demand";
    const heatmap = useDemandHeatmap({ enabled: demandActive, sinceMinutes: 60 });
    // Feed the alert rail — apply local ACKs so cards fold to "acknowledged"
    // without waiting for the next WS frame.
    // Phase E stage-safety: if the URL carries ?force-bunch=1, seed a
    // synthetic bunching alert so the receipt-printer card fires on cue
    // even when the current feed has no live forecasts.
    const railAlerts = useMemo(() => {
        const real = alerts.map((a) => ackedIds.has(a.id)
            ? { ...a, displayState: "acknowledged" }
            : a);
        if (real.length > 0)
            return real;
        if (typeof window !== "undefined" &&
            new URLSearchParams(window.location.search).get("force-bunch") === "1") {
            const now = Math.floor(Date.now() / 1000);
            return [
                {
                    id: "alert-stagedemo",
                    ts: now,
                    route_id: "AS-4",
                    vehicles: ["AS-4-B02-d1", "AS-4-B03-d1"],
                    forecast_gap_s: 42,
                    forecast_lead_s: 6,
                    suggested_hold_s: 42,
                    suggested_hold_bus: "AS-4-B02-d1",
                    suggested_hold_stop: "Daurulla Border · R-AS-4",
                    state: "active",
                    displayState: "active",
                    headline: "Route AS-4 · AS-4-B02-d1 → AS-4-B03-d1 · pair in 6s",
                },
            ];
        }
        return real;
    }, [alerts, ackedIds]);
    const hold = useHoldCommand();
    // Playwright override: mirror `?hold=<state>` into the hook once.
    useMemoRunOnce(() => {
        if (forcedHold)
            hold.force(forcedHold);
    });
    // Bridge alert-card click → hold panel state machine + POST /api/hold.
    // We pass a real object even though the panel derives its label from
    // the FIRST active alert (which drives suggestedSeconds too).
    const primaryAlert = useMemo(() => railAlerts.find((a) => a.displayState === "active") ?? null, [railAlerts]);
    const dispatchHoldFor = useCallback(async (alert) => {
        setAckedAt((prev) => {
            const next = new Map(prev);
            next.set(alert.id, Date.now());
            return next;
        });
        // Brief `arming` beat so the operator sees an unmistakable armed frame
        // before the request goes on the wire — the 8-state matrix is visible
        // from a single click on the alert card. 220ms is well under the
        // 300ms interaction budget (§7 hallmark) and long enough to render.
        hold.force("arming");
        await new Promise((r) => window.setTimeout(r, 220));
        const res = await hold.execute({
            vehicle_id: alert.suggested_hold_bus,
            stop_id: alert.suggested_hold_stop,
            hold_seconds: alert.suggested_hold_s,
            reason: `alert:${alert.id}`,
            controller: "manual",
        });
        return res;
    }, [hold]);
    const handleAlertHold = useCallback((alertId) => {
        const target = railAlerts.find((a) => a.id === alertId);
        if (!target)
            return;
        void dispatchHoldFor(target);
    }, [railAlerts, dispatchHoldFor]);
    const handleAlertDismiss = useCallback((alertId) => {
        setAckedAt((prev) => {
            const next = new Map(prev);
            next.set(alertId, Date.now());
            return next;
        });
    }, []);
    // Sim traces → chart-shape adapters. Empty envelopes are rendered as
    // "no trace" state inside ABChart so the shell always mounts.
    const daganzoStub = useMemo(() => (daganzoTrace ? toSimTraceStub(daganzoTrace) : null), [daganzoTrace]);
    const mpcStub = useMemo(() => (mpcTrace ? toSimTraceStub(mpcTrace) : null), [mpcTrace]);
    const durationS = mpcStub?.duration_s ?? daganzoStub?.duration_s ?? 3_600;
    const overviewSlides = useMemo(() => [
        {
            id: "alerts",
            title: `Bunching · ${railAlerts.length}`,
            subtitle: "Alert rail · one clay card per viewport",
            badge: _jsx(MockPill, { kind: "live", label: "ROUTE AS-4" }),
            content: (_jsx(BunchingRail, { alerts: railAlerts, connectionState: connectionState, onHold: handleAlertHold, onDismiss: handleAlertDismiss })),
        },
        {
            id: "copilot",
            title: "Ops Copilot",
            subtitle: "Ask about any bus, alert, or the A/B run",
            badge: _jsx(MockPill, { kind: "live", label: "GPT" }),
            content: _jsx(CopilotCard, { alerts: railAlerts, envelope: liveEnvelope }),
        },
        {
            id: "stop-cam",
            title: "Stop-cam · demand",
            subtitle: "Waiting-crowd count → λ_downstream",
            badge: _jsx(MockPill, { kind: "simulated" }),
            content: (_jsx(CctvCard, { kind: "stop", videoSrc: `/videos/${stopCamClip}.mp4`, bboxSrc: `/videos/${stopCamClip}.bboxes.json`, signalLabel: "\u03BB_downstream (pax/min)", wiring: "Feeds the demand-weighted holding term (Berrebi 2015 \u03B2 \u00B7 \u03BB). Hot stop coming up \u2192 next bus holds less.", targetLabel: stopCamTarget.label, fallbackCount: stopCamTarget.fallback })),
        },
        {
            id: "bus-cam",
            title: "In-bus cam · occupancy",
            subtitle: "Onboard count → C_residual",
            badge: _jsx(MockPill, { kind: "simulated" }),
            content: (_jsx(CctvCard, { kind: "bus", videoSrc: "/videos/bus-cam-real-1.mp4", bboxSrc: "/videos/bus-cam-real-1.bboxes.json", signalLabel: "C_residual (seats left)", wiring: "Feeds the full-bus discount \u2014 never hold a bus nobody can board. Fires in 1/48 holds in the tuned regime.", targetLabel: "AS-4-B02 \u00B7 fleet MH-01-BM-1234", fallbackCount: 22 })),
        },
        {
            id: "ride-requests",
            title: "Ride requests · queue",
            subtitle: "Rider → admin · auto-nearest assign",
            badge: _jsx(MockPill, { kind: "live", label: "CARPOOL" }),
            content: _jsx(RideRequestsCard, { hook: carpoolRequests }),
        },
        {
            id: "carpool",
            title: "Carpool bridge · live",
            subtitle: "OSRM-routed drivers · surge cap · book flow",
            badge: _jsx(MockPill, { kind: "live", label: "OSRM" }),
            content: (_jsx(CarpoolTrustCard, { anchorLat: carpoolAnchor.lat, anchorLng: carpoolAnchor.lng })),
        },
        {
            id: "autopilot",
            title: "Driver autopilot · voice",
            subtitle: "Turn-by-turn cues, no screen",
            badge: _jsx(MockPill, { kind: "simulated" }),
            content: _jsx(DriverAutopilotCard, { alerts: railAlerts }),
        },
    ], [railAlerts, connectionState, handleAlertHold, handleAlertDismiss, phantomStrip, liveEnvelope, carpoolAnchor, stopCamClip, stopCamTarget.label, stopCamTarget.fallback, carpoolRequests]);
    const rightRail = activeNav === "docs" ? (_jsx(DocsPanel, {})) : demandActive ? (_jsx(PrivacyPanel, { cells: heatmap.cells, status: heatmap.status, error: heatmap.error, generatedAt: heatmap.generatedAt, sinceMinutes: heatmap.sinceMinutes, onSimulate: () => heatmap.refetch() })) : (_jsx(RailCarousel, { slides: overviewSlides }));
    return (_jsxs("div", { style: rootStyle, "data-testid": "app-shell", "data-active-nav": activeNav, children: [_jsx(Header, { activeKey: activeNav, onNavChange: onNavChange, connectionState: connectionState, connectionError: connectionError, reconnectAttempts: reconnectAttempts, envelope: liveEnvelope ?? undefined, carpoolImpact: carpoolRequests.impact }), _jsx(PoolMergeSplash, { merges: carpoolRequests.merges, latestMergeAt: carpoolRequests.latestMergeAt }), _jsxs("main", { style: mainStyle, className: "bb-main", children: [_jsx("div", { style: mapPaneStyle, className: "bb-map-pane", "data-testid": "map-pane", children: _jsx(BusMap, { envelope: envelope, demandActive: demandActive, heatmapCells: heatmap.cells, heatmapWindowMinutes: heatmap.sinceMinutes, phantomLayerSlot: resolvedPhantomLayer, onBusClick: setSelectedBus, carpoolDrivers: carpool.data?.drivers, carpoolAnchor: carpoolAnchor, activeCarpoolRides: carpoolRequests.active }) }), _jsx("div", { className: "bb-rail-pane", "data-testid": "rail-pane", style: {
                            // Explicit bounded height so nested overflow:auto containers
                            // (RailCarousel body, BunchingRail alert stack) actually know
                            // where to stop and start scrolling. Grid children default to
                            // align-items:stretch but some browsers still need an explicit
                            // height to bind percentage children below.
                            minWidth: 0,
                            height: "100%",
                            minHeight: 0,
                            overflow: "hidden",
                        }, children: rightRail })] }), _jsx("div", { "aria-hidden": !stripOpen, style: {
                    position: "fixed",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 40,
                    transform: stripOpen ? "translateY(0)" : "translateY(100%)",
                    transition: "transform 260ms cubic-bezier(0.19, 1, 0.22, 1)",
                    pointerEvents: stripOpen ? "auto" : "none",
                    boxShadow: stripOpen ? "0 -12px 32px rgba(0,13,16,0.28)" : "none",
                }, children: _jsx(BottomStrip, { phantom: phantomStrip, hold: _jsx(HoldCommandPanel, { forcedState: forcedHold ?? hold.state, suggestedSeconds: primaryAlert?.suggested_hold_s ?? 42, vehicleId: primaryAlert?.suggested_hold_bus ?? "AS-4-B02", stopId: primaryAlert?.suggested_hold_stop ?? "AS-4-S04", history: hold.history, lastError: hold.lastError, onPrimary: () => {
                            if (forcedHold)
                                return; // Playwright freeze
                            if (hold.state === "idle" || hold.state === "arming") {
                                hold.advance();
                                return;
                            }
                            if (hold.state === "armed" && primaryAlert) {
                                void dispatchHoldFor(primaryAlert);
                                return;
                            }
                            if (hold.state === "ack-timeout" || hold.state === "error") {
                                if (primaryAlert)
                                    void dispatchHoldFor(primaryAlert);
                                return;
                            }
                        } }), sim: _jsx(SimControls, { durationSeconds: durationS, activeController: activeController, onControllerChange: setActiveController }), chart: _jsx(ABChart, { daganzo: daganzoStub, mpc: mpcStub, loading: tracesLoading, error: tracesError, activeController: activeController }) }) }), _jsxs("button", { type: "button", onClick: () => setStripOpen((v) => !v), "aria-expanded": stripOpen, "aria-controls": "bb-bottom-strip", "data-testid": "strip-toggle", style: {
                    position: "fixed",
                    right: 424, // 400px rail + 24px margin
                    bottom: stripOpen ? 268 : 20, // ~260px strip height + 8px gap
                    transition: "bottom 260ms cubic-bezier(0.19, 1, 0.22, 1)",
                    zIndex: 50,
                    height: 36,
                    padding: "0 16px",
                    background: "var(--color-deep-ink)",
                    color: "var(--color-pure-white)",
                    border: "0",
                    borderRadius: 999,
                    fontFamily: "var(--font-display)",
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    boxShadow: "0 4px 16px rgba(0,13,16,0.28)",
                }, children: [_jsx("span", { style: { display: "inline-block", transform: stripOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 260ms" }, children: "\u25B2" }), stripOpen ? "Hide sim & chart" : "Sim & chart"] }), selectedBus ? (_jsx(BusCctvModal, { vehicle: selectedBus, onClose: () => setSelectedBus(null), onHold: (v) => {
                    const alertForBus = railAlerts.find((a) => a.suggested_hold_bus === v.vehicle.id) ??
                        railAlerts.find((a) => a.displayState === "active");
                    if (alertForBus) {
                        void dispatchHoldFor(alertForBus);
                    }
                    setSelectedBus(null);
                } })) : null, _jsx("style", { children: `
        @media (max-width: 960px) {
          .bb-main {
            grid-template-columns: minmax(0, 1fr) !important;
          }
          .bb-map-pane {
            height: 60vh !important;
            min-height: 320px !important;
          }
          .bb-rail-pane {
            border-top: 1px solid var(--color-pebble);
          }
        }
        @media (min-width: 1200px) {
          .header-route-meta {
            display: block !important;
          }
        }
        /* Hide native scrollbars on the nav pill container */
        [data-testid="primary-nav"]::-webkit-scrollbar { display: none; }
        @media (max-width: 480px) {
          .bb-corridor-caption {
            max-width: 200px !important;
            padding: 6px 10px !important;
          }
          .bb-corridor-caption > div:nth-child(2) { font-size: 13px !important; }
          .bb-corridor-caption > div:nth-child(3) { display: none !important; }
        }
      ` })] }));
}
// ------------------------------------------------------------------
// Tiny mount-only helper. React 18 StrictMode fires effects twice —
// this guards a genuinely one-time transition into a hook. We use it
// only for the ?hold=<state> URL override.
// ------------------------------------------------------------------
function useMemoRunOnce(fn) {
    useMemo(fn, []); // eslint-disable-line react-hooks/exhaustive-deps
}
